import { BusinessRounded, FlagRounded } from '@mui/icons-material'
import { Box, Card, CardActionArea, CardActions, CardContent, CardMedia, ListItemButton, Tooltip, Typography, styled } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { ReactElement } from 'react'
import { useNavigate } from 'react-router'
import useScreenWidth from '../../hooks/useScreenWidth'
import HiddenMobile from '../../styles/HiddenMobile'
import TooltipCards from '../TooltipCards'
import FavoriteButton from '../buttons/FavoriteButton'
import Visibility from './Visibility'

type CardHeroProps = {
  type: number
  redirect: string
  cardBrandName?: string
  cardVisibility: number
  cardImage: string
  cardTitle: string
  cardDescription?: ReactElement
  cardDate?: string
  cardActions?: ReactElement
  onClickCard?: () => void
  favoriteState?: boolean
  onClickFavorite?: (state: boolean) => void
}

const GridContainerCard = styled(Grid)(({ theme }) => ({
  '&.MuiGrid-root': {
    display: 'flex',
    [theme.breakpoints.up('lg')]: {
      display: 'inherit'
    },
    [theme.breakpoints.down('md')]: {
      '& > div:nth-child(1)': {
        paddingRight: 10
      }
    },
    [theme.breakpoints.up('sm')]: {
      '& img': {
        height: '190px'
      }
    },
    [theme.breakpoints.down('sm')]: {
      '& img': {
        height: '100%'
      }
    }
  }
}))

const CardTextHome = styled(CardContent)(({ theme }) => ({
  padding: 0,
  paddingBottom: 8,
  ' & h6': {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
    overflow: 'hidden'
  },
  '&:last-child': {
    paddingBottom: 16,
    paddingTop: 0
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '1rem'
  }
}))

const ContainerFeed = styled(ListItemButton)(({ theme }) => ({
  padding: ' 8px 16px',
  '& svg:': {
    height: 18,
    width: 14
  },
  '& p': {
    maxWidth: 207,
    letterSpacing: '0.0015em',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
    overflow: 'hidden'
  },
  [theme.breakpoints.down('sm')]: {
    '& p': {
      maxWidth: 250
    }
  }
}))

function CardHero({
  type,
  redirect,
  cardVisibility,
  cardImage,
  cardTitle,
  cardDescription,
  cardDate,
  cardBrandName = '',
  onClickCard = () => {},
  cardActions = <></>,
  favoriteState = false,
  onClickFavorite
}: CardHeroProps) {
  const isMobile = useScreenWidth(700)
  const navigate = useNavigate()

  const handleRedirect = () => {
    navigate(redirect)
    onClickCard()
  }

  return (
    <>
      {type === 1 ? (
        <Card
          sx={theme => ({
            width: '250px',
            height: '340px',
            gap: '16px',
            display: 'flex',
            boxShadow: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important',
            position: 'relative',
            borderRadius: '8px',
            backgroundColor: theme.palette.common.white,
            '&::last-child': {
              marginBottom: '16px'
            }
          })}
        >
          <GridContainerCard container>
            <Grid xs={4} md={12}>
              <CardMedia
                sx={{
                  '.MuiCardMedia-root': {
                    borderRadius: '8px 8px 8px 0px',
                    '&:hover': {
                      cursor: 'pointer'
                    }
                  }
                }}
                height={190}
                component="img"
                image={cardImage}
                alt={cardTitle}
                onClick={() => handleRedirect()}
              />
            </Grid>
            <Grid xs={8} md={12} style={{ padding: 8, paddingTop: 0 }}>
              <CardContent
                sx={theme => ({
                  padding: '2px',
                  paddingBottom: '0px',
                  [theme.breakpoints.down('sm')]: {
                    padding: '8px'
                  }
                })}
              >
                <Box onClick={() => handleRedirect()} style={{ cursor: 'pointer' }}>
                  <TooltipCards titleCard={cardTitle} classNameCustom="cardHeroTitle" variantCustom="subtitle1" />
                  <Typography variant="caption" color="textSecondary" style={{ paddingTop: '5px' }}>
                    {cardDate}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions style={{ padding: 5, justifyContent: 'space-between' }} disableSpacing>
                <Box display="flex">
                  {onClickFavorite && <FavoriteButton handleClick={onClickFavorite} favoriteState={favoriteState} />}
                  {cardActions}
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  pr={isMobile ? '6px' : 2}
                  onClick={() => handleRedirect()}
                  style={{ cursor: 'pointer' }}
                >
                  <Box>
                    <Visibility type={cardVisibility} />
                  </Box>
                  <Box>
                    <HiddenMobile display="flex" alignItems="center">
                      {cardBrandName !== null && (
                        <>
                          <BusinessRounded fontSize="small" htmlColor="#8F99A3" />
                          <Typography variant="body2" color="textSecondary">
                            {cardBrandName}
                          </Typography>
                        </>
                      )}
                    </HiddenMobile>
                  </Box>
                </Box>
              </CardActions>
            </Grid>
          </GridContainerCard>
        </Card>
      ) : type === 0 ? (
        // Usado para la lista de destacados ultimos 3 elementos
        <ContainerFeed divider onClick={() => handleRedirect()}>
          <FlagRounded sx={theme => ({ fill: theme.palette.primary.main })} />
          <Box pl={2}>
            <TooltipCards titleCard={cardTitle} classNameCustom="cardHeroType0" />
            <Typography variant="caption" color="textSecondary">
              {cardDate}
            </Typography>
          </Box>
        </ContainerFeed>
      ) : type === 3 ? (
        <Card style={{ backgroundColor: '#FAFAFA' }} elevation={0}>
          <Tooltip title={cardTitle}>
            <CardActionArea onClick={() => handleRedirect()}>
              <Box>
                <CardMedia
                  sx-={{
                    borderRadius: '8px 8px 8px 0px',
                    '&:hover': {
                      cursor: 'pointer'
                    }
                  }}
                  component="img"
                  alt={cardTitle}
                  height="200px"
                  image={cardImage}
                />
                <CardContent
                  sx={{
                    padding: '0px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '0 !important',
                    display: 'grid',
                    '& p': {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      WebkitLineClamp: 3,
                      overflow: 'hidden',
                      lineHeight: '19px',
                      fontWeight: 500
                    }
                  }}
                >
                  <Typography align="left" variant="body2">
                    {cardTitle}
                  </Typography>
                </CardContent>
              </Box>
            </CardActionArea>
          </Tooltip>
        </Card>
      ) : type === 4 ? (
        <Card
          sx={theme => ({
            boxShadow: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important',
            position: 'relative',
            borderRadius: '8px',
            backgroundColor: theme.palette.common.white,
            '&::last-child': {
              marginBottom: '16px'
            }
          })}
          style={{ padding: 8 }}
        >
          <CardActionArea onClick={() => handleRedirect()}>
            <GridContainerCard container>
              <Grid lg={12}>
                <CardMedia component="img" image={cardImage} height="240" alt={cardTitle} />
              </Grid>
              <Grid lg={12}>
                <CardTextHome style={{ textAlign: 'initial' }}>
                  <TooltipCards titleCard={cardTitle} classNameCustom="cardHerotitleCorousel" variantCustom="subtitle1" />
                  <Typography variant="caption" color="textSecondary">
                    {cardDate}
                  </Typography>
                </CardTextHome>
              </Grid>
            </GridContainerCard>
          </CardActionArea>
        </Card>
      ) : (
        <Card style={{ backgroundColor: '#FAFAFA' }}>
          <CardActionArea onClick={() => handleRedirect()}>
            <GridContainerCard container>
              <Grid xs={4} lg={12}>
                <CardMedia component="img" alt={cardTitle} height="135" image={cardImage} />
              </Grid>
              <Grid xs={8} lg={12}>
                <CardContent
                  sx={theme => ({
                    [theme.breakpoints.down('sm')]: {
                      paddingLeft: '0.5rem'
                    },
                    [theme.breakpoints.down('md')]: {
                      paddingLeft: '1rem'
                    },
                    padding: '1rem',
                    paddingTop: '16px'
                  })}
                >
                  <TooltipCards titleCard={cardTitle} classNameCustom="cardHeroUnderline" variantCustom="subtitle1" />
                  <Typography
                    variant="body2"
                    sx={theme => ({
                      display: 'none',
                      [theme.breakpoints.up('sm')]: {
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: 3,
                        overflow: 'hidden'
                      },
                      [theme.breakpoints.up('md')]: {
                        display: 'none'
                      }
                    })}
                  >
                    {cardDescription}
                  </Typography>
                  <Typography color="textSecondary">{cardDate}</Typography>
                </CardContent>
              </Grid>
            </GridContainerCard>
          </CardActionArea>
          <CardActions>{cardActions}</CardActions>
        </Card>
      )}
    </>
  )
}

export default CardHero
